import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  channelDeclaresTab,
  foldChannelPages,
  isChannelListTab,
  type ChannelDetails,
  type ChannelListTab,
  type ChannelPage,
  type ChannelTab,
  type ChannelVideoSort,
  type InstanceConfig,
  type PlaylistSummary,
  type VideoSummary,
} from '@visious/core';
import {
  fetchChannelDetails,
  fetchChannelPlaylistPage,
  fetchChannelSearch,
  fetchChannelVideoPage,
  type ChannelSearchResult,
} from '../../api/channels';
import type { ApiProblem } from '../../api/videos';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import {
  channelSearchState,
  playlistTabState,
  toChannelAbout,
  toChannelHeader,
  toSortMenu,
  toTabLinks,
  videoTabState,
  type ChannelAbout,
  type ChannelHeader,
  type ChannelListState,
  type ChannelTabLink,
} from './model';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import type { MenuModel } from '../../ui/menuModel';
import { channelProblemSubject } from './model';

/**
 * The React binding for §8's channel page. Every decision is in `model.ts` and in core; what is here
 * is the two queries and the clock.
 *
 * **`useInfiniteQuery`, unlike the comment list.** `useComments.ts` says why *it* is hand-rolled — a
 * second paged list per expanded thread, a sort substituted mid-flight, and a hook that cannot be
 * called in a loop. None of that applies here: one list, one identity, append-only, and the cursor is
 * a token the previous page handed over, which is exactly the shape `getNextPageParam` exists for.
 * Writing a second accumulator by hand would be the "two owners" mistake 0e paid for.
 *
 * `retry: false` for the reason 1c established and 1e re-confirmed: §3's retry, backoff and per-origin
 * live in `createApiTransport`. 2d adds a sharper case — an unbrowsable channel is
 * `upstream-broken`, which the transport already refuses to retry, and a second loop here would
 * reinstate the three pointless requests that classification exists to remove.
 */
export interface ChannelBinding {
  readonly tab: ChannelTab;
  readonly sort: ChannelVideoSort;
  /** `null` until the record arrives — the header and the tab strip cannot be drawn without it. */
  readonly header: ChannelHeader | null;
  readonly tabs: readonly ChannelTabLink[];
  /** `null` on the two tabs whose endpoint has no working sort — see `toSortMenu`. */
  readonly sortMenu: MenuModel | null;
  readonly about: ChannelAbout | null;
  /** The channel record failing replaces the whole page; a tab failing replaces only the list. */
  readonly problem: ProblemCopy | null;
  readonly list: ChannelListState | null;
  readonly loadMore: () => void;
  readonly retry: () => void;
  readonly refreshing: boolean;
}

/**
 * The config rides along the way it does on the Watch page: §3's instance resolution is asynchronous
 * while the About tab's jump-out links are built during render, and re-bootstrapping to learn the
 * display origin would be asking §3 a question this load has already answered.
 */
interface ChannelLoad {
  readonly record: ChannelDetails;
  readonly config: InstanceConfig;
}

async function loadDetails(channelId: string): Promise<ChannelLoad> {
  const boot = await getPlatform();
  if (!boot.ok) throw problemError({ kind: 'bad-url', path: `/api/v1/channels/${channelId}` });
  const result = await fetchChannelDetails(boot.platform, channelId);
  if (!result.ok) throw problemError(result.error);
  return { record: result.value, config: boot.platform.config };
}

async function loadVideoPage(
  channelId: string,
  tab: Exclude<ChannelListTab, 'playlists'>,
  sort: ChannelVideoSort,
  continuation: string | null,
): Promise<ChannelPage<VideoSummary>> {
  const boot = await getPlatform();
  if (!boot.ok) throw problemError({ kind: 'bad-url', path: `/api/v1/channels/${channelId}/${tab}` });
  const result = await fetchChannelVideoPage(boot.platform, channelId, tab, sort, continuation);
  if (!result.ok) throw problemError(result.error);
  return result.value;
}

async function loadPlaylistPage(
  channelId: string,
  sort: ChannelVideoSort,
  continuation: string | null,
): Promise<ChannelPage<PlaylistSummary>> {
  const boot = await getPlatform();
  if (!boot.ok) throw problemError({ kind: 'bad-url', path: `/api/v1/channels/${channelId}/playlists` });
  const result = await fetchChannelPlaylistPage(boot.platform, channelId, sort, continuation);
  if (!result.ok) throw problemError(result.error);
  return result.value;
}

/**
 * The transport returns its failures as values, and `useInfiniteQuery` needs them as throws to stop
 * paging — so this is the one place the two conventions meet. The `ApiProblem` rides on the error
 * rather than being flattened to a message, because `describeProblem` needs the classified kind.
 */
class ProblemError extends Error {
  constructor(readonly problem: ApiProblem) {
    super('api problem');
  }
}

function problemError(problem: ApiProblem): ProblemError {
  return new ProblemError(problem);
}

function problemOf(error: unknown): ApiProblem {
  if (error instanceof ProblemError) return error.problem;
  // A genuine throw — a bug, or an abort. Reported as a network failure, the honest reading of "no
  // answer came back".
  return { kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } };
}

export function useChannel(
  channelId: string,
  tab: ChannelTab,
  sort: ChannelVideoSort,
  search: { readonly q: string; readonly page: number } | null,
): ChannelBinding {
  const details = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => loadDetails(channelId),
    retry: false,
    // Shared across every tab of the channel: the header, the tab strip and About all come from this
    // one record, so switching tabs must not re-fetch 50 KB to redraw the same header.
    staleTime: 10 * 60 * 1000,
  });

  const listTab = search === null && isChannelListTab(tab) ? tab : null;
  const videoTab = listTab === null || listTab === 'playlists' ? null : listTab;

  /**
   * Two queries rather than one with a union return, and it is not only a types argument: the two
   * envelopes carry different row shapes (`{videos}` and `{playlists}`), and folding them through one
   * accumulator would mean a cast at exactly the place a mistake would be invisible. Only one is ever
   * `enabled`, so this costs one idle query object.
   *
   * The sort is part of the key, and it has to be: a continuation **carries its own order**, so "this
   * tab sorted differently" is a genuinely different list rather than a re-ordering of this one.
   * Sharing a cache entry would append oldest-first pages under a newest-first heading.
   *
   * `getNextPageParam` reads the continuation and nothing else. Not the page size: `/playlists`
   * returned 29 rows on page 7 of twelve, so `rows < 30` would have stopped a channel's playlists two
   * thirds of the way through and looked like the end.
   */
  const videoPages = useInfiniteQuery({
    queryKey: ['channelTab', channelId, videoTab, sort],
    enabled: videoTab !== null,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => loadVideoPage(channelId, videoTab ?? 'videos', sort, pageParam),
    getNextPageParam: (last: ChannelPage<VideoSummary>) => last.continuation,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const playlistPages = useInfiniteQuery({
    queryKey: ['channelTab', channelId, 'playlists', sort],
    enabled: listTab === 'playlists',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => loadPlaylistPage(channelId, sort, pageParam),
    getNextPageParam: (last: ChannelPage<PlaylistSummary>) => last.continuation,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  /*
   * The in-channel search follows the search page's shape rather than the tabs': the cursor is a
   * page *number* (no continuation exists, §7f), the route's own `page` is where the list starts,
   * and a failed page is a **value** — `channelSearchState` reads the refusal itself, because "page
   * N+1 of a set that had N" is this endpoint's only end-of-list signal and must not stop the fold.
   */
  const searchPages = useInfiniteQuery({
    queryKey: ['channelSearch', channelId, search?.q, search?.page],
    enabled: search !== null,
    initialPageParam: search?.page ?? 1,
    queryFn: async ({ pageParam }): Promise<ChannelSearchResult> => {
      const boot = await getPlatform();
      if (!boot.ok) {
        return { ok: false, error: { kind: 'bad-url', path: `/api/v1/channels/${channelId}/search` } };
      }
      return fetchChannelSearch(boot.platform, channelId, search?.q ?? '', pageParam);
    },
    getNextPageParam: (_last, all) => (search?.page ?? 1) + all.length,
    retry: false,
    // A search is a snapshot of a ranking that is not even stable between identical requests (§7f) —
    // re-fetching on a back-navigation would change the page under the reader for no reason.
    staleTime: 5 * 60 * 1000,
  });

  const pages =
    search !== null ? searchPages : listTab === 'playlists' ? playlistPages : videoPages;

  const nowEpochSec = useMemo(() => {
    // Relative dates anchored to when the rows *arrived*, not to this render — 2b's rule. With paging
    // that means the first page's arrival, so a list left open does not creep row by row as pages land.
    const arrivedAt = pages.dataUpdatedAt > 0 ? pages.dataUpdatedAt : systemClock.nowEpochMs();
    return Math.floor(arrivedAt / 1000);
  }, [pages.dataUpdatedAt]);

  const progress = useWatchProgressLookup();

  const list = useMemo((): ChannelListState | null => {
    if (search !== null) {
      return channelSearchState(search.q, search.page, searchPages.data?.pages ?? null, {
        nowEpochSec,
        loadingMore: searchPages.isFetchingNextPage,
        progress,
      });
    }
    if (listTab === null) return null;
    /*
     * `declared` needs the channel record, which arrives on its own request — so while that is in
     * flight it is `true`, i.e. "assume the tab is a real one". The alternative would flash "this
     * channel has no Shorts tab" under a list that is still loading, on every channel that does have
     * one. By the time an empty list can be rendered the record is normally already here.
     */
    const options = {
      nowEpochSec,
      loadingMore: pages.isFetchingNextPage,
      declared: details.data === undefined || channelDeclaresTab(details.data.record.tabs, listTab),
      progress,
    };

    if (listTab === 'playlists') {
      return playlistTabState(
        playlistPages.data === undefined ? null : foldChannelPages(playlistPages.data.pages),
        playlistPages.error === null ? null : problemOf(playlistPages.error),
        options,
      );
    }

    return videoTabState(
      listTab,
      videoPages.data === undefined ? null : foldChannelPages(videoPages.data.pages),
      videoPages.error === null ? null : problemOf(videoPages.error),
      options,
    );
  }, [
    details.data,
    listTab,
    nowEpochSec,
    pages.isFetchingNextPage,
    progress,
    playlistPages.data,
    playlistPages.error,
    search,
    searchPages.data,
    searchPages.isFetchingNextPage,
    videoPages.data,
    videoPages.error,
  ]);

  const record = details.data?.record ?? null;
  const searchListMore = list !== null && list.kind === 'ready' && list.loadMore === 'available';

  return {
    tab,
    sort,
    header: record === null ? null : toChannelHeader(record),
    tabs:
      record === null
        ? []
        : // No tab is the place a search is at — the strip stays for the way back, unmarked.
          toTabLinks(record, tab, sort).map((link) =>
            search === null ? link : { ...link, current: false },
          ),
    // Derived from the *route's* tab rather than from the record, so the sort control renders while the
    // channel record is still in flight — the same argument 2c's filter bar is built on. Absent while
    // searching: the endpoint ignores `sort_by` (§7f), and a control that cannot do anything is §8's
    // "shown broken".
    sortMenu: search === null ? toSortMenu(tab, sort) : null,
    about: record === null ? null : toChannelAbout(record, details.data?.config ?? null),
    problem:
      details.error === null
        ? null
        : describeProblem(problemOf(details.error), channelProblemSubject()),
    list,
    loadMore: () => {
      // The search fold owns its own end (a refused page is a value, so `hasNextPage` cannot know) —
      // the tabs' cursor is a continuation the query machinery does track.
      if (search !== null) {
        if (searchListMore && !searchPages.isFetchingNextPage) void searchPages.fetchNextPage();
        return;
      }
      if (pages.hasNextPage && !pages.isFetchingNextPage) void pages.fetchNextPage();
    },
    retry: () => {
      if (details.error !== null) void details.refetch();
      void pages.refetch();
    },
    refreshing: (details.isFetching && !details.isPending) || (pages.isFetching && !pages.isPending),
  };
}
